import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import ScorecardNavigation from './ScorecardNavigation';
import MonthFilter from './MonthFilter';
import ErrorMessage from '../common/ErrorMessage';
import LoadingSpinner from '../common/LoadingSpinner';
import ScorecardDropdown, { type DropdownOption } from '../common/ScorecardDropdown';
import {
  storeScorecardData,
  storeSpecificData,
  getAvailableMonths,
  getLatestMonth,
  filterDataByMonth,
  type StoreScorecardEntry
} from '../../data/scorecardData';
import managementService from '../../services/managementService';
import { config } from '../../config/environment';
import ShiftMetricsTable from './ShiftMetricsTable';

interface StoreScorecardProps {
  storeName?: string;
}

interface Location {
  id: string;
  name: string;
  created: string;
}

const StoreScorecard: React.FC<StoreScorecardProps> = ({ storeName = 'Fremont' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedTenant } = useTenant();
  
  // Get store data from location state or use defaults
  const storeData = location.state?.storeData || { storeName };
  
  // State for real data
  const [storeEntries, setStoreEntries] = useState<StoreScorecardEntry[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedStore, setSelectedStore] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [managerNames, setManagerNames] = useState<Record<string, string>>({});

  // Track if we've restored filters to prevent infinite loops
  const [hasRestoredFilters, setHasRestoredFilters] = useState(false);
  
  // Get store-specific data or fall back to default
  const currentStoreData = storeSpecificData[storeData.storeName] || storeScorecardData;
  
  const [selectedMonth, setSelectedMonth] = useState(() => {
    if (config.useDummyData) {
      return getLatestMonth(currentStoreData);
    }
    // For real data, we'll set this after data is loaded to the first available month
    return '';
  });

  // Normalize role to handle case-insensitive comparison
  const normalizedRole = useMemo(() => {
    const rawRole = user?.managementUser?.role;
    if (!rawRole) return null;
    return rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase();
  }, [user?.managementUser?.role]);

  // Determine user role and permissions
  const isAdmin = normalizedRole === 'Admin';
  const isCorporateManager = normalizedRole === 'Corporate';
  const isStoreManager = normalizedRole === 'Manager';
  const userLocationId = user?.managementUser?.location_id;
  const userTenantId = user?.managementUser?.tenant_id;

  // Determine location filter based on user role
  const getLocationFilter = useMemo(() => {
    if (config.useDummyData) {
      return undefined;
    }
    
    if (!user?.managementUser || !normalizedRole) {
      return undefined;
    }
    
    const { location_id } = user.managementUser;
    
    if (normalizedRole === 'Admin') {
      const filter = selectedStore || undefined;
      console.log('StoreScorecard: getLocationFilter (Admin) =', filter, 'selectedStore =', selectedStore);
      return filter;
    } else if (normalizedRole === 'Corporate') {
      const filter = selectedStore || undefined;
      console.log('StoreScorecard: getLocationFilter (Corporate) =', filter, 'selectedStore =', selectedStore);
      return filter;
    } else if (normalizedRole === 'Manager') {
      return location_id;
    }
    
    return undefined;
  }, [user?.managementUser, selectedStore, normalizedRole]);

  // Determine tenant filter based on user role
  const getTenantFilter = useMemo(() => {
    if (config.useDummyData) {
      return undefined;
    }
    
    if (!user?.managementUser || !normalizedRole) {
      return undefined;
    }
    
    const { tenant_id } = user.managementUser;
    
    if (normalizedRole === 'Admin') {
      return selectedTenant || undefined;
    } else if (normalizedRole === 'Corporate') {
      return selectedTenant || tenant_id || undefined;
    } else if (normalizedRole === 'Manager') {
      return selectedTenant || tenant_id || undefined;
    }
    
    return undefined;
  }, [user?.managementUser, selectedTenant, normalizedRole]);


  // Fetch locations based on user role and selected tenant
  useEffect(() => {
    const fetchLocations = async () => {
      // Fetch locations for admin, corporate manager, and store manager users
      if (!isAdmin && !isCorporateManager && !isStoreManager) return;
      
      // For admin users, don't fetch if no tenant is selected
      if (isAdmin && !selectedTenant) {
        // Clear locations and selected store when no tenant is selected
        setLocations([]);
        setSelectedStore('');
        return;
      }
      
      setIsLoadingLocations(true);
      try {
        // Use getTenantFilter to ensure Corporate and Store managers always use their tenant
        const tenantId = getTenantFilter;
        
        const response = await managementService.getLocationsByTenant(tenantId || undefined);
        if (response.success && response.locations) {
          setLocations(response.locations);
          // Set initial selected store to first location for admins (only if not coming from leaderboard and not restoring filters)
          const savedFilters = location.state?.filters;
          if (response.locations.length > 0 && !storeData.storeId && !savedFilters && !hasRestoredFilters && !selectedStore) {
            setSelectedStore(response.locations[0].id);
          }
        } else {
          setError(response.error || 'Failed to fetch locations');
        }
      } catch (err) {
        setError('Failed to fetch locations');
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchLocations();
  }, [isAdmin, isCorporateManager, isStoreManager, getTenantFilter, location.state, hasRestoredFilters, selectedStore]);

  // Clear data when tenant changes (for admin users)
  useEffect(() => {
    if (isAdmin && selectedTenant) {
      // When tenant changes, clear all data and reset selections
      setStoreEntries([]);
      setManagerNames({});
      setSelectedStore('');
      setSelectedMonth('');
      setAvailableMonths([]);
      setError(null);
      setHasRestoredFilters(false);
    }
  }, [selectedTenant, isAdmin]);

  // Restore filters when returning from details page
  useEffect(() => {
    const savedFilters = location.state?.filters;
    if (savedFilters && !hasRestoredFilters) {
      // Restore selectedStore if it exists (always restore, even if it matches, to ensure it's set)
      if (savedFilters.selectedStore) {
        setSelectedStore(savedFilters.selectedStore);
      }
      // Restore selectedMonth if it exists (always restore, even if it matches, to ensure it's set)
      if (savedFilters.selectedMonth) {
        setSelectedMonth(savedFilters.selectedMonth);
      }
      setHasRestoredFilters(true);
    } else if (!savedFilters && hasRestoredFilters) {
      // Reset the flag when there are no saved filters (new navigation)
      setHasRestoredFilters(false);
    }
  }, [location.state, hasRestoredFilters]);

  // Set initial selected store based on user role (only if we haven't restored from filters)
  useEffect(() => {
    // Don't override if we've restored filters or if we're waiting to restore
    const savedFilters = location.state?.filters;
    if (hasRestoredFilters || savedFilters) {
      return;
    }
    
    if (isStoreManager && userLocationId) {
      // Store managers see only their location
      setSelectedStore(userLocationId);
    } else if (storeData.storeId) {
      // If a specific store is selected from leaderboard, use that
      setSelectedStore(storeData.storeId);
    } else if ((isAdmin || isCorporateManager) && locations.length > 0 && !selectedStore) {
      // For admins and corporate managers, set to first location if none selected
      setSelectedStore(locations[0].id);
    }
  }, [isAdmin, isCorporateManager, isStoreManager, userLocationId, storeData.storeId, locations.length, hasRestoredFilters, selectedStore, location.state]);

  // Fetch all store data and detect available months in a single request
  const fetchStoreDataAndMonths = async (storeId: string, preserveCurrentMonth: boolean = false) => {
    if (config.useDummyData || !storeId) return;

    // Check if we have saved filters that should be preserved
    const savedFilters = location.state?.filters;
    const shouldPreserveMonth = preserveCurrentMonth || (savedFilters && savedFilters.selectedMonth);
    const monthToPreserve = savedFilters?.selectedMonth;

    setIsLoading(true);
    setError(null);
    try {
      // Single fetch with wide date range — gets all data at once
      const locationIdToUse = getLocationFilter || storeId;
      const tenantFilter = getTenantFilter && getTenantFilter !== null ? getTenantFilter : undefined;
      console.log('StoreScorecard: Fetching all data with location:', locationIdToUse, 'tenant:', tenantFilter);
      const response = await managementService.getStoreScorecardData(locationIdToUse, '2020-01-01', '2030-12-31', tenantFilter);

      if (response.success && response.entries) {
        // Store all entries — month filtering happens client-side via filteredData useMemo
        setStoreEntries(response.entries);

        // Detect available months from the fetched data
        const monthsWithData = new Set<string>();
        response.entries.forEach(entry => {
          const date = new Date(entry.date);
          const monthString = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long'
          });
          monthsWithData.add(monthString);
        });

        if (monthsWithData.size > 0) {
          const sortedMonths = Array.from(monthsWithData).sort((a, b) => {
            const dateA = new Date(a);
            const dateB = new Date(b);
            return dateB.getTime() - dateA.getTime();
          });

          setAvailableMonths(sortedMonths);

          if (shouldPreserveMonth) {
            setSelectedMonth(currentMonth => {
              const monthToKeep = monthToPreserve || currentMonth;
              if (monthToKeep === 'All') return 'All';
              if (monthToKeep && !monthsWithData.has(monthToKeep)) return sortedMonths[0];
              return monthToKeep || currentMonth;
            });
          } else {
            setSelectedMonth(currentMonth => {
              if (currentMonth === 'All') return 'All';
              if (!currentMonth || !monthsWithData.has(currentMonth)) return sortedMonths[0];
              return currentMonth;
            });
          }
        } else {
          setAvailableMonths([]);
        }

        // Fetch manager names for all unique manager IDs
        if (response.entries.length > 0) {
          const uniqueManagerIds = Array.from(new Set(response.entries.map(entry => entry.manager_id)));
          const managerNamePromises = uniqueManagerIds.map(async (managerId) => {
            const managerResponse = await managementService.getManagementUserById(managerId);
            if (managerResponse.success && managerResponse.user) {
              return {
                id: managerId,
                name: `${managerResponse.user.first_name} ${managerResponse.user.last_name}`
              };
            }
            return { id: managerId, name: `Manager ${managerId.slice(0, 8)}` };
          });

          const managerNameResults = await Promise.all(managerNamePromises);
          const managerNameMap = managerNameResults.reduce((acc, { id, name }) => {
            acc[id] = name;
            return acc;
          }, {} as Record<string, string>);
          setManagerNames(managerNameMap);
        } else {
          setManagerNames({});
        }
      } else {
        setError(response.error || 'Failed to fetch store data');
      }
    } catch (err) {
      console.error('StoreScorecard - Error fetching store data:', err);
      setError('Failed to fetch store data');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when store is selected (single request for everything)
  useEffect(() => {
    if (selectedStore) {
      fetchStoreDataAndMonths(selectedStore, hasRestoredFilters);
    } else {
      setStoreEntries([]);
      setManagerNames({});
      setAvailableMonths([]);
    }
  }, [selectedStore, hasRestoredFilters, getTenantFilter]);

  // State for available months across all locations
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);

  // For dummy data, get available months from the data
  const dummyAvailableMonths = useMemo(() => {
    if (config.useDummyData) {
      return getAvailableMonths(currentStoreData);
    }
    return [];
  }, [currentStoreData]);

  // Filter data by selected month. feat-093 Slice C: the backend score is
  // rendered VERBATIM — the client no longer overrides it with a Revel-adjusted
  // recompute (feedback_metrics_single_source). We only normalize the shift label.
  const filteredData = useMemo(() => {
    if (config.useDummyData) {
      return filterDataByMonth(currentStoreData, selectedMonth);
    }

    // For real data, filter storeEntries by selected month
    if (storeEntries.length === 0) {
      return [];
    }

    const normalizeShift = (entry: StoreScorecardEntry) => ({
      ...entry,
      shift: entry.shift ? entry.shift.charAt(0).toUpperCase() + entry.shift.slice(1).toLowerCase() : 'Unknown'
    });

    // Handle "All" selection - return all entries
    if (selectedMonth === 'All') {
      return storeEntries.map(normalizeShift);
    }

    // Filter by specific month
    return storeEntries.filter(entry => {
      if (entry.date) {
        const date = new Date(entry.date);
        // Handle timezone issues - use UTC methods to get consistent month/year
        const monthString = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          timeZone: 'UTC' // Use UTC to avoid timezone conversion issues
        });
        // Also try without timezone in case the date is already in local time
        const monthStringLocal = date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long'
        });
        // Normalize selectedMonth (remove comma if present for comparison)
        const normalizedSelected = selectedMonth.replace(',', '');
        const normalizedMonthString = monthString.replace(',', '');
        const normalizedMonthStringLocal = monthStringLocal.replace(',', '');

        return normalizedMonthString === normalizedSelected || normalizedMonthStringLocal === normalizedSelected;
      }
      return false;
    }).map(normalizeShift);
  }, [currentStoreData, selectedMonth, storeEntries]);

  // Get store name for display
  const getStoreName = (storeId: string): string => {
    if (config.useDummyData) {
      return storeData.storeName;
    }
    const location = locations.find(loc => loc.id === storeId);
    return location ? location.name : 'Unknown Store';
  };

  // Handle store change for admin users
  const handleStoreChange = (storeId: string) => {
    console.log('StoreScorecard: Store changed to:', storeId);
    setSelectedStore(storeId);
  };

  // Convert locations to dropdown options
  const storeOptions: DropdownOption[] = useMemo(() => {
    return locations.map(location => ({
      value: location.id,
      label: location.name
    }));
  }, [locations]);

  const handleDetailsClick = (date: string, shift: string, manager: string, score: number, shiftId: string, managerId: string, created?: string, ended?: string) => {
    navigate('/restaurant/store-shift-details', {
      state: {
        shiftData: {
          date,
          shift,
          score,
          manager,
          store: getStoreName(selectedStore),
          shiftId,
          managerId,
          created,
          ended
        },
        // Save current filter state so we can restore it when coming back
        filters: {
          selectedStore,
          selectedMonth
        }
      } 
    });
  };

  // Calculate overall month-to-date average score from filtered data
  const monthToDateScore = filteredData.length > 0 
    ? Math.round(filteredData.reduce((sum, entry) => sum + entry.score, 0) / filteredData.length)
    : 0;

  // Calculate scores by manager from filtered data
  const scoresByManager = filteredData.reduce((acc, entry) => {
    const managerName = managerNames[entry.manager_id] || `Manager ${entry.manager_id.slice(0, 8)}`;
    if (!acc[managerName]) {
      acc[managerName] = { scores: [], count: 0 };
    }
    acc[managerName].scores.push(entry.score);
    acc[managerName].count += 1;
    return acc;
  }, {} as Record<string, { scores: number[]; count: number }>);

  // Calculate average scores for each manager
  const managerAverages = Object.entries(scoresByManager).map(([manager, data]) => ({
    manager,
    averageScore: Math.round(data.scores.reduce((sum, score) => sum + score, 0) / data.scores.length),
    shiftCount: data.count
  }));

  // Check if admin user needs to select a tenant (this must be first!)
  const isAdminWithoutTenant = isAdmin && !selectedTenant;

  if (isAdminWithoutTenant) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Tenant</h3>
              <p className="text-gray-600 mb-4">
                Please select a tenant from the dropdown in the navigation bar to view store performance data.
              </p>
              <div className="text-sm text-gray-500">
                Use the tenant selector in the top-left corner to choose which tenant's data to view.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading spinner while fetching data or detecting months
  if (isLoading && !config.useDummyData) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <LoadingSpinner message="Loading store data..." />
        </div>
      </div>
    );
  }

  // Show error message if there's an error and we're not loading
  if (error && !config.useDummyData && !isLoading) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <ErrorMessage message={error} />
        </div>
      </div>
    );
  }

  // Show empty state when no locations are available for admin/corporate manager
  if (!config.useDummyData && (isAdmin || isCorporateManager) && !isLoadingLocations && locations.length === 0 && selectedTenant) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Locations Found</h3>
              <p className="text-gray-600 mb-4">
                This tenant doesn't have any store locations configured yet.
              </p>
              <div className="text-sm text-gray-500">
                Store locations need to be added before viewing store scorecard data.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show prompt to select a store when tenant is selected but no store is selected (for admin users)
  if (!config.useDummyData && isAdmin && selectedTenant && !isLoadingLocations && locations.length > 0 && !selectedStore && !isLoading) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Store Location</h3>
              <p className="text-gray-600 mb-4">
                Please select a store location from the dropdown above to view store performance data.
              </p>
              <div className="text-sm text-gray-500">
                Use the store location dropdown in the header to choose which store's data to view.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Navigation */}
      <ScorecardNavigation />
      
      <div className="p-6">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h1 className="text-4xl font-display font-semibold text-ink">Store Scorecard</h1>
            {config.useDummyData ? (
              <span className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md">
                {storeData.storeName}
              </span>
            ) : (
              <>
                {(isAdmin || isCorporateManager) ? (
                  <ScorecardDropdown
                    value={selectedStore}
                    onChange={handleStoreChange}
                    options={storeOptions}
                    loading={isLoadingLocations}
                    loadingText="Loading locations..."
                    variant="inline"
                  />
                ) : (
                  <span className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md">
                    {isLoadingLocations ? 'Loading...' : getStoreName(selectedStore)}
                  </span>
                )}
              </>
            )}
          </div>
          <MonthFilter
            availableMonths={config.useDummyData ? dummyAvailableMonths : availableMonths}
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
          />
        </div>

      {/* Stats Blocks */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        {/* Month to Date Score Block */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
            Month to Date Score
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {monthToDateScore}
          </div>
        </div>

        {/* Scores by Manager Block */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
            Scores by Manager Month to Date
          </div>
          <div className="space-y-3">
            {managerAverages.map((manager, index) => (
              <div key={index} className="flex justify-between items-center">
                <div className="text-sm font-medium text-gray-700">
                  {manager.manager}
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    manager.averageScore >= 95 ? 'bg-green-100 text-green-800' :
                    manager.averageScore >= 90 ? 'bg-yellow-100 text-yellow-800' :
                    manager.averageScore >= 85 ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {manager.averageScore}
                  </span>
                  <span className="text-xs text-gray-500">
                    ({manager.shiftCount} shifts)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-clip">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Scores from Shifts This Month</h2>
        </div>
        <ShiftMetricsTable
          // feat-297: pinned header; the card above is overflow-clip (not -hidden) so the sticky reaches the page.
          pinHeader
          shifts={filteredData.map(row => ({
            date: row.date,
            type: row.shift || 'Unknown',
            // feat-093 Slice C: Visits = backend visit_count (?? greetings),
            // rendered verbatim. The legacy `greetings` slot is unused here.
            greetings: row.greetings ?? 0,
            visitCount: row.visit_count ?? row.greetings ?? undefined,
            score: row.score,
            premiumMenu: 0,
            itemMention: 0,
            itemDescription: 0,
            drinkPromotions: 0,
            managerName: managerNames[row.manager_id] || `Manager ${row.manager_id.slice(0, 8)}`,
            shiftId: row.shift_id || row.manager_id,
          }))}
          showDate={true}
          showGreetings={true}
          showMetrics={false}
          showManager={true}
          emptyMessage="No store scorecard data available"
          onViewDetails={(row) => {
            const originalRow = filteredData.find(r => (r.shift_id || r.manager_id) === row.shiftId);
            if (originalRow) {
              handleDetailsClick(
                originalRow.date,
                originalRow.shift,
                managerNames[originalRow.manager_id] || `Manager ${originalRow.manager_id.slice(0, 8)}`,
                originalRow.score,
                originalRow.shift_id || originalRow.manager_id,
                originalRow.manager_id,
                originalRow.date,
                undefined
              );
            }
          }}
        />
      </div>

      {/* Empty State */}
      {filteredData.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">No store scorecard data available</div>
          <div className="text-gray-400 text-sm mt-2">
            {config.useDummyData 
              ? 'Check back later for store performance data'
              : 'No shifts have been recorded for this store yet'}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default StoreScorecard;
